import {useNavigate, useParams} from "react-router-dom";
import {Resolver, useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {LotModelNew, LotQueryParamsNew} from "../../../../data/model/Lot";
import {editLotSchema, lotSchema} from "../../../../data/schema/lotSchema";
import {
    HStack,
    Input,
    Select,
    Spinner,
    Stack,
    Table,
    TableContainer,
    Tbody,
    Text,
    useToast,
    VStack
} from "@chakra-ui/react";
import {CheckCircleIcon} from "@chakra-ui/icons";
import {BackButtonComponent} from "../../../../components/molecules/BackButton/BackButtonComponent";
import * as React from "react";
import {useEffect, useState} from "react";
import {addLotNew, editLotNew, getLotsByIdNew, getProductsMinifyNew} from "../../../../data/api/Customer";
import {scrollbarCss} from "../../../../theme/theme";
import {AddTableRow} from "../../../../components/atoms/AddTableRow/AddTableRow";
import {MultiSelect, Option} from "react-multi-select-component";
import {useQuery} from "@tanstack/react-query";
import {BaseResponse} from "../../../../data/model/BaseResponse";
import {ProductModelNew} from "../../../../data/model/Product";

export const AddProductLotPage = () => {
    const navigate = useNavigate();
    const toast = useToast();

    const {id} = useParams();

    const responseData = useQuery({
        queryKey: ['get-lots', id],
        enabled: !!id,
        queryFn: () => getLotsByIdNew(id || ""),
        refetchOnWindowFocus: "always",
        staleTime: 0
    });

    const {data: dataOld, refetch} = responseData || {};

    const lotResponse = dataOld?.data as BaseResponse<LotModelNew | undefined>;
    const lot = lotResponse?.data;

    const userType = localStorage.getItem("userType");
    const {data} = useQuery({queryKey: ["product-mine-minify"], queryFn: () => getProductsMinifyNew({userType})})

    const methods = useForm<LotQueryParamsNew>({
        mode: "all",
        resolver: yupResolver(id ? editLotSchema : lotSchema) as Resolver<LotQueryParamsNew>
    });

    const {
        register,
        handleSubmit,
        setValue,
        formState: {
            errors,
            isSubmitting,
        }
    } = methods;

    const [selectedProduct, setSelectedProduct] = useState<Option[]>([]);
    const [selectedProductVariant, setSelectedProductVariant] = useState<Option[]>([]);

    const [periodState, setPeriodState] = useState("d");
    const [periodNumber, setPeriodNumber] = useState<string>("");

    const [quantityMeasurement, setQuantityMeasurement] = useState("Unità");

    const {data: dataProduct} = (data?.data as BaseResponse<ProductModelNew[]>) || {};
    const productList = dataProduct || [];

    const options = productList.map(value => ({
        label: value.name || "",
        value: value.id || {}
    })) || [];

    useEffect(() => {
        if (lot) {
            const num = lot.consumeWithin?.split('@')[0];
            const period = lot.consumeWithin?.split('@')[1];

            setPeriodNumber(num || "");
            setPeriodState(period || "d");

            if (lot.unitMeasurement) {
                setQuantityMeasurement(lot.unitMeasurement);
            }

            if (lot.product) {
                setSelectedProduct([{
                    label: lot.product!.name || "",
                    value: lot.product.id || {}
                }]);

                if (lot.variant) {
                    setSelectedProductVariant([{
                        label: `${lot.variant.value}` || "",
                        value: lot.variant.id || {}
                    }])
                }
            }
        }
    }, [lot]);

    const [optionsVariant, setOptionsVariant] = useState<Option[]>([]);

    useEffect(() => {
        let productKey: string = selectedProduct?.[0]?.value as string || "";
        let productModel: ProductModelNew = productList.find(value => value.id === productKey) || {};

        const optionsVariantList: Option[] = [];

        productModel.variants?.forEach(value => {
            optionsVariantList.push({
                label: value.value || "",
                value: value.id || ""
            })
        });

        /*productModel.variants?.forEach(value => {
            optionsVariantList.push(...value?.values?.map(value1 => ({
                label: `${value1.key} ${value1.value}` || "",
                value: value1.id || {}
            })) || []);
        });*/

        setOptionsVariant(optionsVariantList);
    }, [selectedProduct]);

    const handleChange = ({selected}: { selected: Option[] | undefined | null }) => {
        if (!selected || selected.length === 0) {
            setSelectedProduct([]);
            setSelectedProductVariant([]);
            setValue("productId", undefined);
            setValue("variantId", undefined);
        } else if (selectedProduct.length < 1) {
            setSelectedProduct(selected);
            setValue("productId", (selected[0].value as string));
        }
    };

    const handleChangeVariant = ({selected}: { selected: Option[] | undefined | null }) => {
        if (!selected || selected.length === 0) {
            setSelectedProductVariant([]);
            setValue("variantId", undefined);
        } else if (selectedProductVariant.length < 1) {
            setSelectedProductVariant(selected);
            setValue("variantId", (selected[0].value as string));
        }
    };

    const onSubmit = async (lot: LotModelNew) => {
        if (id) {
            await editLotNew(id, lot).then(() => {
                refetch().then(() => navigate(-1));
            }).catch(reason => {
                toast({
                    title: "Si è verificato un errore",
                    description: reason?.message ?? "-"
                });
            });
        } else {
            await addLotNew(lot).then(() => {
                navigate(-1);
            }).catch(reason => {
                toast({
                    title: "Si è verificato un errore",
                    description: reason?.message ?? "-"
                });
            });
        }
    }

    const handleDatetimeChange = (field: "expiration" | "producedOn", event: React.ChangeEvent<HTMLInputElement>) => {
        const timestamp = new Date(event.target.value).getTime() / 1000;
        setValue(field, timestamp.toString());
    };

    const handlePeriodNumber = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const newPeriodNumber = await event.target.value;
        setPeriodNumber(newPeriodNumber);
        setValue("consumeWithin", `${newPeriodNumber}@${periodState}`);
    }

    const handleChangeMeasurement = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setQuantityMeasurement(event.target.value);
        setValue("unitMeasurement", event.target.value);
    }

    const handlePeriodChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setPeriodState(event.target.value);

        let response;
        switch (event.target.value.toLowerCase()) {
            case "mesi":
                response = "m";
                break;
            case "anni":
                response = "y";
                break;
            case "giorni":
            default:
                response = "d";
                break;
        }

        setValue("consumeWithin", `${periodNumber}@${response}`);
    }

    return <VStack w={"100%"} h={"100%"} alignItems={"center"}>
        <HStack cursor={"pointer"} h={"64px"} px={"24px"} borderRadius={"24px"} position={"absolute"}
                alignItems={"center"} zIndex={5}
                justifyContent={"center"} bottom={"24px"} right={"48px"}
                backgroundColor={isSubmitting ? "primary.100" : "secondary.200"}
                _hover={{backgroundColor: "tertiary.200"}}
                onClick={handleSubmit(onSubmit)}>
            {isSubmitting ? <Spinner width={"24px"} height={"24px"}/> :
                <CheckCircleIcon w={"24px"} h={"24px"} color={"primary.400"} alignSelf={"center"}/>}
            <Text fontWeight={"bold"} color={"primary.400"}>Conferma</Text>
        </HStack>
        <BackButtonComponent/>
        <VStack justifyContent={"center"} flex={1} w={"100%"} alignItems={"center"} overflowY={"hidden"}>
            <Text fontSize={"24px"} justifySelf={"center"} fontWeight={"bold"}
                  ms={"24px"}>{id ? "Modifica lotto" : "Nuovo lotto"}</Text>
            <TableContainer alignItems={"center"} whiteSpace={"pre-line"} mr={"16px"} overflowY={"scroll"}
                            css={scrollbarCss({marginRight: 48})}>
                <Table variant={"none"} w={"100%"} mx={"auto"}>
                    <Tbody>
                        <AddTableRow name={"Prodotto"}>
                            <Stack w={"100%"}>
                                <MultiSelect
                                    hasSelectAll={false}
                                    options={options}
                                    value={selectedProduct || []}
                                    onChange={(value: Option[]) => handleChange({selected: value || []})}
                                    labelledBy="Seleziona"
                                />
                            </Stack>
                        </AddTableRow>

                        <AddTableRow name={"Variante"}>
                            <Stack w={"100%"}>
                                <MultiSelect
                                    hasSelectAll={false}
                                    options={optionsVariant}
                                    value={selectedProductVariant || []}
                                    onChange={(value: Option[]) => handleChangeVariant({selected: value || []})}
                                    labelledBy="Seleziona"
                                />
                            </Stack>
                        </AddTableRow>

                        <AddTableRow name={"COD. Lotto"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"code"}
                                    inputMode={"text"}
                                    type={"text"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={lot?.code}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.code}
                                    focusBorderColor={!!errors.code ? "error.700" : "primary.400"}
                                    {...register('code')}
                                    placeholder='Es. LOT-A-001'/>
                                {!!errors.code && <Text fontSize={14}
                                                        alignSelf={"start"}
                                                        textColor={"error.700"}>
                                    {errors.code && errors.code.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Quantità"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"quantity"}
                                    inputMode={"numeric"}
                                    type={"number"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={lot?.quantity}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.quantity}
                                    focusBorderColor={!!errors.quantity ? "error.700" : "primary.400"}
                                    {...register('quantity')}
                                    placeholder='Es. 50'/>
                                {!!errors.quantity && <Text fontSize={14}
                                                            alignSelf={"start"}
                                                            textColor={"error.700"}>
                                    {errors.quantity && errors.quantity.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Quantità per prodotto"}>
                            <HStack>
                                <VStack alignItems={"flex-start"} w={"100%"}>
                                    <Input
                                        inputMode={"numeric"}
                                        type={"number"}
                                        colorScheme={"primary"}
                                        variant='filled'
                                        defaultValue={lot?.quantityForUnit}
                                        _placeholder={{fontSize: 14}}
                                        errorBorderColor={"error.700"}
                                        disabled={isSubmitting}
                                        isInvalid={!!errors.quantityForUnit}
                                        focusBorderColor={!!errors.quantityForUnit ? "error.700" : "primary.400"}
                                        {...register('quantityForUnit')}
                                        // onChange={(event: React.ChangeEvent<HTMLInputElement>) => handleQuantityForUnit(event)}
                                        placeholder='Es. 5'/>
                                    {!!errors.quantityForUnit && <Text fontSize={14}
                                                                       alignSelf={"start"}
                                                                       textColor={"error.700"}>
                                        {errors.quantityForUnit && errors.quantityForUnit.message}
                                    </Text>}
                                </VStack>
                                <Stack w={"100%"}>
                                    <Select placeholder="Seleziona l'unità di misura"
                                            value={quantityMeasurement}
                                            defaultValue={"Unità"}
                                            onChange={event => handleChangeMeasurement(event)}>
                                        <option>g</option>
                                        <option>Kg</option>
                                        <option>mL</option>
                                        <option>L</option>
                                        <option>Unità</option>
                                    </Select>
                                </Stack>
                            </HStack>
                        </AddTableRow>

                        <AddTableRow name={"Prezzo"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"price"}
                                    inputMode={"numeric"}
                                    type={"number"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={lot?.price}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.price}
                                    focusBorderColor={!!errors.price ? "error.700" : "primary.400"}
                                    {...register('price')}
                                    placeholder='Es. 50.00'/>
                                {!!errors.price && <Text fontSize={14}
                                                         alignSelf={"start"}
                                                         textColor={"error.700"}>
                                    {errors.price && errors.price.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Prezzo scontato"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"price"}
                                    inputMode={"numeric"}
                                    type={"number"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={lot?.discountPrice}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.discountPrice}
                                    focusBorderColor={!!errors.discountPrice ? "error.700" : "primary.400"}
                                    {...register('discountPrice')}
                                    placeholder='Es. 50.00'/>
                                {!!errors.discountPrice && <Text fontSize={14}
                                                                 alignSelf={"start"}
                                                                 textColor={"error.700"}>
                                    {errors.discountPrice && errors.discountPrice.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Spese di spedizione"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"shipping_fee"}
                                    inputMode={"numeric"}
                                    type={"number"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={lot?.shippingFee}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.shippingFee}
                                    focusBorderColor={!!errors.shippingFee ? "error.700" : "primary.400"}
                                    {...register('shippingFee')}
                                    placeholder='Es. 50.00'/>
                                {!!errors.shippingFee && <Text fontSize={14}
                                                                 alignSelf={"start"}
                                                                 textColor={"error.700"}>
                                    {errors.shippingFee && errors.shippingFee.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Prodotto il"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"producedOn"}
                                    inputMode={"numeric"}
                                    type={"date"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    defaultValue={getDefaultDateTime(lot?.producedOn)}
                                    _placeholder={{fontSize: 14}}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    isInvalid={!!errors.producedOn}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                            value += "T00:00";
                                        }
                                        handleDatetimeChange("producedOn", { target: { value } } as React.ChangeEvent<HTMLInputElement>);
                                    }}
                                    focusBorderColor={!!errors.producedOn ? "error.700" : "primary.400"}
                                    placeholder='Es. 01/01/2024 00:00'/>
                                {!!errors.producedOn && <Text fontSize={14}
                                                              alignSelf={"start"}
                                                              textColor={"error.700"}>
                                    {errors.producedOn && errors.producedOn.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Scadenza"}>
                            <VStack alignItems={"flex-start"} w={"100%"}>
                                <Input
                                    id={"expiration"}
                                    inputMode={"numeric"}
                                    type={"date"}
                                    colorScheme={"primary"}
                                    variant='filled'
                                    _placeholder={{fontSize: 14}}
                                    defaultValue={getDefaultDateTime(lot?.expiration)}
                                    errorBorderColor={"error.700"}
                                    disabled={isSubmitting}
                                    onChange={(e) => {
                                        let value = e.target.value;
                                        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
                                            value += "T00:00";
                                        }
                                        handleDatetimeChange("expiration", { target: { value } } as React.ChangeEvent<HTMLInputElement>);
                                    }}
                                    isInvalid={!!errors.expiration}
                                    focusBorderColor={!!errors.expiration ? "error.700" : "primary.400"}
                                    placeholder='Es. 01/01/2024 00:00'/>
                                {!!errors.expiration && <Text fontSize={14}
                                                              alignSelf={"start"}
                                                              textColor={"error.700"}>
                                    {errors.expiration && errors.expiration.message}
                                </Text>}
                            </VStack>
                        </AddTableRow>

                        <AddTableRow name={"Consumare entro"}>
                            <HStack>
                                <VStack alignItems={"flex-start"} w={"100%"}>
                                    <Input
                                        id={"consumeWithin"}
                                        inputMode={"numeric"}
                                        type={"number"}
                                        colorScheme={"primary"}
                                        variant='filled'
                                        defaultValue={lot?.consumeWithin?.split('@')?.[0]}
                                        _placeholder={{fontSize: 14}}
                                        errorBorderColor={"error.700"}
                                        disabled={isSubmitting}
                                        isInvalid={!!errors.consumeWithin}
                                        focusBorderColor={!!errors.consumeWithin ? "error.700" : "primary.400"}
                                        onChange={(event: React.ChangeEvent<HTMLInputElement>) => handlePeriodNumber(event)}
                                        placeholder='Es. 5'/>
                                    {!!errors.consumeWithin && <Text fontSize={14}
                                                                     alignSelf={"start"}
                                                                     textColor={"error.700"}>
                                        {errors.consumeWithin && errors.consumeWithin.message}
                                    </Text>}
                                </VStack>
                                <Stack w={"100%"}>
                                    <Select placeholder="Seleziona il periodo"
                                            value={getSelectValue(periodState)}
                                            defaultValue={"d"}
                                            onChange={event => handlePeriodChange(event)}>
                                        <option>Giorni</option>
                                        <option>Mesi</option>
                                        <option>Anni</option>
                                    </Select>
                                </Stack>
                            </HStack>
                        </AddTableRow>
                    </Tbody>
                </Table>
            </TableContainer>
        </VStack>
    </VStack>
}

export function getSelectValue(letter: string) {
    switch (letter) {
        case 'd':
            return "Giorni";
        case 'm':
            return "Mesi";
        case 'y':
            return "Anni";
    }
}

export function getDefaultDateTime(timestamp?: string) {
    if (!timestamp) return undefined
    const defaultDate = new Date(Number(timestamp) * 1000);

    console.log(defaultDate);
    return defaultDate.toISOString().slice(0, 16);
}

export function getDefaultDate(timestamp?: string) {
    if (!timestamp) return undefined
    const defaultDate = new Date(Number(timestamp) * 1000);
    return defaultDate.toISOString().split('T')[0];
}

export function getDefaultTime(timestamp?: string) {
    if (!timestamp) return undefined
    const defaultDate = new Date(Number(timestamp) * 1000);
    return defaultDate.toISOString().split('T')[1].slice(0, 5);
}

// export const handleDeleteMedia = (onDeleteConfirm: () => void) => {
//     const {} = useMutation({mutationKey: ["delete-media", id], mutationFn: () => deleteMediaNew(id)});
//
//     const confirmDelete = window.confirm("Sei sicuro di voler eliminare questo elemento?");
//     if (confirmDelete) {
//         console.log("Elemento eliminato.");
//     } else {
//         console.log("Eliminazione annullata.");
//     }
// };
